import { ArrowLeft, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const StepsDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", steps: 9200 },
    { day: "Tue", steps: 7800 },
    { day: "Wed", steps: 10500 },
    { day: "Thu", steps: 8900 },
    { day: "Fri", steps: 11200 },
    { day: "Sat", steps: 6500 },
    { day: "Sun", steps: 8206 },
  ];

  const monthlyData = [
    { week: "Week 1", steps: 8900 },
    { week: "Week 2", steps: 9500 },
    { week: "Week 3", steps: 8200 },
    { week: "Week 4", steps: 9100 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Steps</h1>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-chart-1/10 to-chart-1/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">Today's Steps</p>
            <div className="mb-2 text-5xl font-bold text-foreground">8,206</div>
            <p className="text-sm text-muted-foreground">of 10,000 goal</p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="bg-chart-1 h-full rounded-full"
                style={{ width: "82%" }}
              />
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">22.4</div>
            <div className="text-xs text-muted-foreground">km Distance</div>
          </Card>
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">420</div>
            <div className="text-xs text-muted-foreground">Calories</div>
          </Card>
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">8</div>
            <div className="text-xs text-muted-foreground">Floors</div>
          </Card>
        </div>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-2 p-1">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="steps"
                    fill="hsl(var(--chart-1))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="steps"
                    fill="hsl(var(--chart-1))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Information Card */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="bg-info-light flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl">
              <Info className="text-info h-5 w-5" />
            </div>
            <div>
              <h3 className="mb-2 font-semibold text-foreground">
                How Steps Are Tracked
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Your steps are automatically tracked using your Fitbit device's
                accelerometer. The sensor detects motion patterns that indicate
                walking or running. Each step contributes to your daily activity
                goal and helps burn calories. Walking 10,000 steps daily is
                recommended for maintaining good health and fitness.
              </p>
            </div>
          </div>
        </Card>

        {/* Benefits Card */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-secondary/5 to-secondary/10 p-5 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            Health Benefits
          </h3>
          <ul className="space-y-2">
            {[
              "Improves cardiovascular health",
              "Helps maintain healthy weight",
              "Strengthens bones and muscles",
              "Boosts mood and energy levels",
              "Reduces risk of chronic diseases",
            ].map((benefit, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <span className="mt-0.5 text-secondary">•</span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default StepsDetail;
